import { useTranslation } from "react-i18next";
import { IContractDetailsResponseData } from "../../../../setup/api/services/contract/models";
import styles from "./styles.module.scss";
import { useContext, useMemo } from "react";
import PartyTable from "../../create/partyTable";
import { EPARTY_STATUS } from "../../../../enums";
import AmountCard from "../AmountCard";
import { ContractContext } from "../../../../providers/contract";

interface CashoutDetailsProps {
  milestone: IContractDetailsResponseData | undefined;
}

const CashoutDetails = ({ milestone }: CashoutDetailsProps) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const accountId = contract?.organization?.accountId;

  const customers = useMemo(() => {
    return milestone?.partiesProfiles?.CUSTOMER?.filter(
      (customer) =>
        milestone.cashOutAmountList?.findIndex(
          (party) =>
            party.userIdentification === String(customer.userIdentification) &&
            (party.amountDue || 0) > 0
        ) !== -1
    );
  }, [milestone?.cashOutAmountList, milestone?.partiesProfiles?.CUSTOMER]);

  const providers = useMemo(() => {
    return milestone?.partiesProfiles?.PROVIDER?.filter(
      (provider) =>
        milestone.cashOutAmountList?.findIndex(
          (party) =>
            party.userIdentification === String(provider.userIdentification) &&
            (party.amountDue || 0) > 0
        ) !== -1
    );
  }, [milestone?.cashOutAmountList, milestone?.partiesProfiles?.PROVIDER]);

  const brokers = useMemo(() => {
    return milestone?.partiesProfiles?.BROKER?.filter(
      (broker) =>
        milestone.cashOutAmountList?.findIndex(
          (party) =>
            party.userIdentification === String(broker.userIdentification) &&
            (party.amountDue || 0) > 0
        ) !== -1
    );
  }, [milestone?.cashOutAmountList, milestone?.partiesProfiles?.BROKER]);

  const amountSettled = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    return admin?.amountDue !== undefined
      ? admin?.amountDue + (milestone?.fee || 0)
      : 0;
  }, [milestone?.cashOutAmountList, milestone?.fee, accountId]);

  const profit = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    return admin?.amountDue !== undefined ? admin?.amountDue : 0;
  }, [milestone?.cashOutAmountList, accountId]);

  return (
    <>
      {!!customers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_payees")}</h2>

          <PartyTable
            detailsPage
            rows={customers?.map((party) => {
              const currentParty = milestone?.parties?.find(
                (partyObject) =>
                  partyObject?.phoneNumber === party?.phoneNumber ||
                  partyObject?.userId == party?.userIdentification
              );
              const cashOutAmountParty = milestone?.cashOutAmountList?.find(
                (cashOutAmount) =>
                  cashOutAmount.userIdentification ===
                  String(party.userIdentification)
              );
              const partyTransactions = milestone?.transactions.filter(
                (transaction) =>
                  (transaction.beneficiaryId === String(party?.id) ||
                    transaction.beneficiaryId ===
                      String(party?.userIdentification)) &&
                  (transaction.type === "CASH_OUT" ||
                    transaction.type === "REFUND_CUSTOMER")
              );
              const partyLastTransaction = partyTransactions?.length
                ? partyTransactions[partyTransactions.length - 1]
                : null;
              return {
                id: party?.id?.toString() || "",
                name: party?.profile?.fullName || "",
                phoneNumber: party?.phoneNumber || "",
                amount: cashOutAmountParty?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.REFUNDED ||
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOutAmountParty?.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "customer",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: party?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {!!providers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_receivers")}</h2>

          <PartyTable
            detailsPage
            rows={providers?.map((party) => {
              const currentParty = milestone?.parties?.find(
                (partyObject) =>
                  partyObject?.phoneNumber === party?.phoneNumber ||
                  partyObject?.userId == party?.userIdentification
              );
              const cashOutAmountParty = milestone?.cashOutAmountList?.find(
                (cashOutAmount) =>
                  cashOutAmount.userIdentification ===
                  String(party.userIdentification)
              );
              const partyTransactions = milestone?.transactions.filter(
                (transaction) =>
                  (transaction.beneficiaryId === String(party?.id) ||
                    transaction.beneficiaryId ===
                      String(party?.userIdentification)) &&
                  (transaction.type === "CASH_OUT" ||
                    transaction.type === "RELEASE_TO_PROVIDER" ||
                    transaction.type === "CLOSE_RELEASE_TO_PROVIDER")
              );
              const partyLastTransaction = partyTransactions?.length
                ? partyTransactions[partyTransactions.length - 1]
                : null;
              return {
                id: party?.id?.toString() || "",
                name: party?.profile?.fullName || "",
                phoneNumber: party?.phoneNumber || "",
                amount: cashOutAmountParty?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOutAmountParty?.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: party?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {!!brokers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_brokers")}</h2>

          <PartyTable
            detailsPage
            rows={brokers?.map((party) => {
              const currentParty = milestone?.parties?.find(
                (partyObject) =>
                  partyObject?.phoneNumber === party?.phoneNumber ||
                  (!!partyObject?.userId &&
                    partyObject?.userId === String(party?.userIdentification))
              );
              const cashOutAmountParty = milestone?.cashOutAmountList?.find(
                (cashOutAmount) =>
                  cashOutAmount.userIdentification ===
                  String(party.userIdentification)
              );
              const partyTransactions = milestone?.transactions.filter(
                (transaction) =>
                  (transaction.beneficiaryId === String(party?.id) ||
                    transaction.beneficiaryId ===
                      String(party?.userIdentification)) &&
                  (transaction.type === "CASH_OUT" ||
                    transaction.type === "RELEASE_TO_PROVIDER" ||
                    transaction.type === "CLOSE_RELEASE_TO_PROVIDER")
              );
              const partyLastTransaction = partyTransactions?.length
                ? partyTransactions[partyTransactions.length - 1]
                : null;
              return {
                id: party?.id?.toString() || "",
                name: party?.profile?.fullName || "",
                phoneNumber: party?.phoneNumber || "",
                amount: cashOutAmountParty?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOutAmountParty?.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: party?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      <div className={styles.cardsContainer}>
        <AmountCard
          title={t("lbl_value_assigned_to_you")}
          amount={String(amountSettled?.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_waffy_fees_amount")}
          amount={String((milestone?.fee || 0)?.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_your_profit")}
          amount={String(profit?.toFixed(2))}
          style={{
            backgroundColor: profit === 0 ? "#F8F9FB" : undefined,
          }}
          specialStyle={
            profit > 0 ? "positive" : profit < 0 ? "negative" : undefined
          }
          isSmall
        />
      </div>
    </>
  );
};

export default CashoutDetails;
